import { useState, useEffect } from "react";
import Sidebar, { dispatchSidebarUpdate } from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { useAuth } from "../Auth-Context/AuthContext";
import {
  Plus, List, LayoutGrid, AlertCircle, X,
  MoreHorizontal, PlusSquare, ArrowRight, Calendar, User, Loader, Trash2, Edit, CheckCircle, SortAsc, SortDesc, Filter,
} from "lucide-react";
import {
  listTasks,
  createTask,
  updateTask,
  updateTaskStatus,
  deleteTask,
  getTaskStats,
  getTasksByDeal,
  getTasksByUser,
  listUsers,
  getContactById
} from "../api/services";
import { listContacts } from "../api/services";
import { listDeals } from "../api/services";
import { AddTaskModal, EditTaskModal, MoveTaskModal } from "../components/TaskModals";

const BOARD_COLS = [
  { id: "todo",       label: "To do",       color: "#f59e0b", apiStatus: "TODO" },
  { id: "inprogress", label: "In Progress", color: "#009DB9", apiStatus: "IN_PROGRESS" },
  { id: "completed",  label: "Completed",   color: "#10b981", apiStatus: "COMPLETED" },
];

const PRIORITY_CHIP = {
  HIGH:   { bg: "#fef2f2", color: "#dc2626", label: "High",   order: 1 },
  MEDIUM: { bg: "#fefce8", color: "#ca8a04", label: "Medium", order: 2 },
  LOW:    { bg: "#f0fdf4", color: "#16a34a", label: "Low",    order: 3 },
};

const AVATAR_COLORS = ["#009DB9", "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899"];

// Roles that can see ALL tasks
const ADMIN_ROLES = ["SUPER_ADMIN", "ADMIN", "MANAGER"];

/* ── helpers ── */
const getUserName = (userId, users, currentUserId) => {
  if (!userId) return null;
  const u = users.find(u => u.id === userId);
  if (!u) return null;
  if (u.id === currentUserId) {
    return `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Current User";
  }
  return u.fullName || `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || userId;
};
const countOpenTasks = (tasksList) =>
  tasksList.filter((t) => t.status === "TODO").length;

/* ── Task card for board ── */
function TaskCard({ task, onMoveClick, onEditClick, onDeleteClick, contactInfo, assignedToName, currentUserRole }) {
  const priority = PRIORITY_CHIP[task.priority] || PRIORITY_CHIP.MEDIUM;
  const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== "COMPLETED";
  const isSalesRep = currentUserRole === "SALES_REP";

  return (
    <div className="flex flex-col gap-1.5 p-2.5 mb-2 transition-all bg-white border rounded-lg hover:-translate-y-px hover:shadow-md group"
      style={{
        borderColor: isOverdue ? "rgba(239,68,68,0.3)" : "#e5e5e5",
        background: task.status === "COMPLETED" ? "#fafafa" : isOverdue ? "#fff8f8" : "#fff",
        opacity: task.status === "COMPLETED" ? 0.65 : 1,
      }}>
      <div className="flex items-start justify-between gap-2">
        <div className={`text-[12px] font-medium leading-snug flex-1 ${task.status === "COMPLETED" ? "line-through text-neutral-400" : "text-neutral-800"}`}>
          {task.title}
        </div>
        <div className="flex items-center gap-0.5 transition-opacity opacity-0 group-hover:opacity-100">
          <button onClick={() => onEditClick(task)} className="p-1 transition-colors rounded hover:bg-neutral-100" title="Edit">
            <Edit size={11} className="text-neutral-400" />
          </button>
          <button onClick={() => onDeleteClick(task)} className="p-1 transition-colors rounded hover:bg-red-50" title="Delete">
            <Trash2 size={11} className="text-red-400" />
          </button>
          <button onClick={() => onMoveClick(task)} className="p-1 transition-colors rounded hover:bg-neutral-100" title="Move">
            <MoreHorizontal size={11} className="text-neutral-400" />
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-1">
        <span className="text-[9px] font-semibold rounded-full px-1.5 py-0.5" style={{ background: priority.bg, color: priority.color }}>{priority.label}</span>
        {task.dueDate && (
          <span className={`flex items-center gap-1 text-[9px] font-medium rounded-full px-1.5 py-0.5 ${isOverdue ? "bg-red-50 text-red-500" : "bg-neutral-100 text-neutral-500"}`}>
            <Calendar size={8} strokeWidth={2} />{new Date(task.dueDate).toLocaleDateString()}
          </span>
        )}
        {isOverdue && (
          <span className="flex items-center gap-1 text-[9px] font-medium bg-red-50 text-red-500 rounded-full px-1.5 py-0.5">
            <AlertCircle size={8} strokeWidth={2} />Overdue
          </span>
        )}
      </div>
      {/* Assigned To - Hide for SALES_REP */}
      {!isSalesRep && assignedToName && (
        <div className="flex items-center gap-1 mt-0.5">
          <User size={8} className="flex-shrink-0 text-neutral-400" />
          <span className="text-[9px] text-neutral-400 truncate">{assignedToName}</span>
        </div>
      )}
      {contactInfo && (
        <div className="pt-1 mt-1 border-t border-neutral-100">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[8px] font-bold flex-shrink-0"
              style={{ background: AVATAR_COLORS[Math.abs(task.id?.length || 0) % AVATAR_COLORS.length] }}>
              {contactInfo.company?.substring(0, 2).toUpperCase() || "NA"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-medium text-neutral-700 truncate">{contactInfo.company}</div>
              <div className="text-[8px] text-neutral-400 truncate">
                {contactInfo.fullName || `${contactInfo.firstName} ${contactInfo.lastName}`}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Main Tasks Component ── */
export default function Tasks() {
  const { user: currentUser } = useAuth();
  const [tasks, setTasks]       = useState([]);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals]       = useState([]);
  const [users, setUsers]       = useState([]);
  const [, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [view, setView]         = useState("list");
  const [listFilter, setListFilter]     = useState("all");
  const [overdueAlert, setOverdueAlert] = useState(true);
  const [showAdd, setShowAdd]           = useState(false);
  const [showEdit, setShowEdit]         = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [moveModal, setMoveModal]       = useState({ open: false, task: null });
  const [searchVal, setSearchVal]       = useState("");
  const [sortBy, setSortBy]             = useState("priority-due");
  const [sortOrder, setSortOrder]       = useState("asc");
  const [showSortMenu, setShowSortMenu]     = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [, setIsMobile] = useState(false);

  const isAdminRole = ADMIN_ROLES.includes(currentUser?.role);
  const isSalesRep = currentUser?.role === "SALES_REP";

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    loadTasks();
    loadContacts();
    loadDeals();
    loadUsers();
    loadStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Role-aware task loader ── */
  const loadTasks = async () => {
    setLoading(true);
    setError(null);
    try {
      let loadedTasks;
      if (isAdminRole) {
        const data = await listTasks({ size: 100 });
        loadedTasks = data.content || [];
      } else {
        const result = await getTasksByUser(currentUser.id);
        loadedTasks = Array.isArray(result) ? result : result.content || [];
      }
      setTasks(loadedTasks);
      dispatchSidebarUpdate({ tasks: countOpenTasks(loadedTasks) });
    } catch (err) {
      console.error("Failed to load tasks:", err);
      setError(err.response?.data?.message || "Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  const loadStats    = async () => { try { setStats(await getTaskStats()); } catch (e) { console.error(e); } };
  const loadContacts = async () => { try { const d = await listContacts({ size: 100 }); setContacts(d.content || []); } catch (e) { console.error(e); } };
  const loadDeals    = async () => { try { const d = await listDeals({ size: 100 });    setDeals(d.content || []); } catch (e) { console.error(e); } };
  const loadUsers    = async () => { try { const d = await listUsers({ size: 100 });    setUsers(d.content || []); } catch (e) { console.error(e); } };

  const handleAddTask = async (taskData) => {
    try {
      const newTask = await createTask(taskData);
      const updatedTasks = [newTask, ...tasks];
      setTasks(updatedTasks);
      // Dispatch updated open task count to sidebar immediately
      dispatchSidebarUpdate({ tasks: countOpenTasks(updatedTasks) });
      await loadStats();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to add task");
      throw err;
    }
  };

  const handleUpdateTask = async (taskId, taskData) => {
    try {
      const updated = await updateTask(taskId, taskData);
      const updatedTasks = tasks.map(t => t.id === taskId ? updated : t);
      setTasks(updatedTasks);
      // Status may have changed during edit — keep sidebar in sync
      dispatchSidebarUpdate({ tasks: countOpenTasks(updatedTasks) });
      await loadStats();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update task");
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    try {
      await deleteTask(taskId);
      const updatedTasks = tasks.filter(t => t.id !== taskId);
      setTasks(updatedTasks);
      // Task removed — update open count in sidebar immediately
      dispatchSidebarUpdate({ tasks: countOpenTasks(updatedTasks) });
      await loadStats();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete task");
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const updated = await updateTaskStatus(taskId, newStatus);
      const updatedTasks = tasks.map(t => t.id === taskId ? updated : t);
      setTasks(updatedTasks);
      // Open count changes when status flips to/from COMPLETED
      dispatchSidebarUpdate({ tasks: countOpenTasks(updatedTasks) });
      await loadStats();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update task");
    }
  };

  const handleMoveTask = async (taskId, newStatus) => {
    try {
      const updated = await updateTaskStatus(taskId, newStatus);
      const updatedTasks = tasks.map(t => t.id === taskId ? updated : t);
      setTasks(updatedTasks);
      setMoveModal({ open: false, task: null });
      // Same as status change — sync sidebar
      dispatchSidebarUpdate({ tasks: countOpenTasks(updatedTasks) });
      await loadStats();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to move task");
    }
  };

  const handleViewTasksByDeal = async (dealId) => {
    if (!dealId) { alert("This task is not associated with a deal"); return; }
    try {
      const dealTasks = await getTasksByDeal(dealId);
      alert(`Found ${dealTasks.length} tasks for this deal`);
    } catch (err) {
      alert("Failed to load tasks for this deal");
    }
  };

  /* ── Sorting: overdue first, then HIGH priority + soonest due date ── */
  const sortTasks = (tasksToSort) => {
    return [...tasksToSort].sort((a, b) => {
      const now = new Date();
      const aOverdue = a.dueDate && new Date(a.dueDate) < now && a.status !== "COMPLETED";
      const bOverdue = b.dueDate && new Date(b.dueDate) < now && b.status !== "COMPLETED";
      if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;

      let comparison = 0;
      switch (sortBy) {
        case "priority-due": {
          const pOrder = { HIGH: 1, MEDIUM: 2, LOW: 3 };
          const pa = pOrder[a.priority] || 99, pb = pOrder[b.priority] || 99;
          if (pa !== pb) { comparison = pa - pb; break; }
          if (a.dueDate && b.dueDate) comparison = new Date(a.dueDate) - new Date(b.dueDate);
          else if (a.dueDate) comparison = -1;
          else if (b.dueDate) comparison = 1;
          break;
        }
        case "priority": {
          const pOrder = { HIGH: 1, MEDIUM: 2, LOW: 3 };
          comparison = (pOrder[a.priority] || 99) - (pOrder[b.priority] || 99);
          break;
        }
        case "due":
          if (a.dueDate && b.dueDate) comparison = new Date(a.dueDate) - new Date(b.dueDate);
          else if (a.dueDate) comparison = -1;
          else if (b.dueDate) comparison = 1;
          break;
        case "title":
          comparison = (a.title || "").localeCompare(b.title || "");
          break;
        default: comparison = 0;
      }
      return sortOrder === "asc" ? comparison : -comparison;
    });
  };

  const getTasksByStatus = (status) => {
    let filtered = tasks.filter(task => {
      if (status === "todo")        return task.status === "TODO";
      if (status === "inprogress")  return task.status === "IN_PROGRESS";
      if (status === "completed")   return task.status === "COMPLETED";
      return false;
    });
    if (priorityFilter !== "all") filtered = filtered.filter(t => t.priority === priorityFilter);
    return sortTasks(filtered);
  };

  const filteredTasks = tasks.filter(task => {
    const contactInfo = contacts.find(c => c.id === task.contactId);
    const matchesSearch = !searchVal ||
      task.title?.toLowerCase().includes(searchVal.toLowerCase()) ||
      contactInfo?.company?.toLowerCase().includes(searchVal.toLowerCase());
    const matchesPriority = priorityFilter === "all" || task.priority === priorityFilter;
    let matchesStatus = true;
    if (listFilter === "todo")        matchesStatus = task.status === "TODO";
    else if (listFilter === "inprogress") matchesStatus = task.status === "IN_PROGRESS";
    else if (listFilter === "done")   matchesStatus = task.status === "COMPLETED";
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const sortedFilteredTasks = sortTasks(filteredTasks);
  const overdueCount = tasks.filter(t => t.dueDate && new Date(t.dueDate) < new Date() && t.status !== "COMPLETED").length;
  const openCount    = tasks.filter(t => t.status !== "COMPLETED").length;

  const LIST_FILTERS = [
    { id: "all",        label: "All" },
    { id: "todo",       label: "To-do" },
    { id: "inprogress", label: "In Progress" },
    { id: "done",       label: "Done" },
  ];

  const SORT_OPTIONS = [
    { id: "priority-due", label: "Priority + Due Date" },
    { id: "priority",     label: "Priority Only" },
    { id: "due",          label: "Due Date" },
    { id: "title",        label: "Task Title" },
  ];

  const statsCards = [
    { label: "Total Tasks", value: tasks.length,                                         icon: List,         color: "#009DB9" },
    { label: "Pending",     value: tasks.filter(t => t.status === "TODO").length,        icon: AlertCircle,  color: "#f59e0b" },
    { label: "In Progress", value: tasks.filter(t => t.status === "IN_PROGRESS").length, icon: ArrowRight,   color: "#009DB9" },
    { label: "Completed",   value: tasks.filter(t => t.status === "COMPLETED").length,   icon: CheckCircle,  color: "#10b981" },
  ];

  const getListGridColumns = () => {
    if (isSalesRep) return "28px 1fr 160px 110px 100px 90px 100px";
    return "28px 1fr 130px 160px 110px 100px 90px 100px";
  };

  const getListHeaders = () => {
    if (isSalesRep) return ["Task", "Contact", "Due Date", "Status", "Priority", "Actions"];
    return ["Task", "Assigned To", "Contact", "Due Date", "Status", "Priority", "Actions"];
  };

  return (
    <div className="flex h-screen overflow-hidden bg-neutral-50">
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <Topbar
          title="Task Management"
          subtitle={`${openCount} open · ${overdueCount} overdue`}
          search={{ value: searchVal, onChange: (v) => setSearchVal(v), placeholder: "Search tasks by title or company..." }}
          actions={[{ label: "New Task", icon: Plus, onClick: () => setShowAdd(true) }]}
        />

        <main className="flex flex-col flex-1 px-4 py-4 overflow-y-auto sm:py-5 sm:px-7">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 gap-3 mb-4 sm:grid-cols-4 sm:gap-4">
            {statsCards.map((card, i) => {
              const Icon = card.icon;
              return (
                <div key={i} className="p-3 bg-white border sm:p-4 rounded-xl border-neutral-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] sm:text-[11px] font-medium text-neutral-500">{card.label}</p>
                      <p className="mt-1 text-xl font-bold sm:text-2xl text-neutral-900">{card.value}</p>
                    </div>
                    <div className="p-2 rounded-lg" style={{ background: `${card.color}10` }}>
                      <Icon size={16} className="sm:w-5 sm:h-5" style={{ color: card.color }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Toggle + filters */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex gap-0.5 bg-neutral-100 rounded-lg p-1">
              {[{ id: "list", icon: List, label: "List" }, { id: "board", icon: LayoutGrid, label: "Board" }].map(({ id, icon: Icon, label }) => (
                <button key={id} onClick={() => setView(id)}
                  className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-md text-[11px] sm:text-[12px] font-medium transition-all"
                  style={view === id ? { background: "#fff", color: "#171717", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" } : { color: "#737373" }}>
                  <Icon size={12} strokeWidth={2} />
                  {label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {!isAdminRole && (
                <span className="flex items-center gap-1 text-[10px] text-neutral-400 bg-neutral-100 px-2 py-1 rounded-full">
                  <User size={10} /> My tasks only
                </span>
              )}

              {/* Priority Filter */}
              <div className="relative">
                <button onClick={() => setShowFilterMenu(!showFilterMenu)}
                  className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-md text-[11px] sm:text-[12px] font-medium bg-white border border-neutral-200 hover:bg-neutral-50">
                  <Filter size={12} />
                  <span>Priority: {priorityFilter === "all" ? "All" : priorityFilter}</span>
                </button>
                {showFilterMenu && (
                  <div className="absolute right-0 z-10 mt-1 bg-white border rounded-lg shadow-lg top-full min-w-[140px]">
                    {["all", "HIGH", "MEDIUM", "LOW"].map(p => (
                      <button key={p} onClick={() => { setPriorityFilter(p); setShowFilterMenu(false); }}
                        className="w-full px-3 py-2 text-left text-[12px] hover:bg-neutral-50 first:rounded-t-lg last:rounded-b-lg">
                        {p === "all" ? "All Priorities" : p}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Sort */}
              <div className="relative">
                <button onClick={() => setShowSortMenu(!showSortMenu)}
                  className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-md text-[11px] sm:text-[12px] font-medium bg-white border border-neutral-200 hover:bg-neutral-50">
                  {sortOrder === "asc" ? <SortAsc size={12} /> : <SortDesc size={12} />}
                  <span>Sort</span>
                </button>
                {showSortMenu && (
                  <div className="absolute right-0 z-10 mt-1 bg-white border rounded-lg shadow-lg top-full min-w-[180px]">
                    {SORT_OPTIONS.map(option => (
                      <button key={option.id} onClick={() => { setSortBy(option.id); setShowSortMenu(false); }}
                        className="w-full px-3 py-2 text-left text-[12px] hover:bg-neutral-50 flex items-center justify-between">
                        <span>{option.label}</span>
                        {sortBy === option.id && <CheckCircle size={12} className="text-[#009DB9]" />}
                      </button>
                    ))}
                    <div className="my-1 border-t border-neutral-100" />
                    <button onClick={() => setSortOrder(s => s === "asc" ? "desc" : "asc")}
                      className="w-full px-3 py-2 text-left text-[12px] hover:bg-neutral-50 flex items-center justify-between">
                      <span>Order</span>
                      <span className="text-neutral-500">{sortOrder === "asc" ? "Ascending" : "Descending"}</span>
                    </button>
                  </div>
                )}
              </div>

              {view === "list" && (
                <div className="flex flex-wrap items-center gap-1.5">
                  {LIST_FILTERS.map((f) => (
                    <button key={f.id} onClick={() => setListFilter(f.id)}
                      className="px-2.5 sm:px-3 h-7 rounded-full text-[11px] sm:text-[12px] font-medium border transition-colors whitespace-nowrap"
                      style={listFilter === f.id
                        ? { background: "#009DB9", color: "#fff", borderColor: "#009DB9" }
                        : { background: "#fff", color: "#525252", borderColor: "#e5e5e5" }}>
                      {f.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Overdue alert */}
          {overdueAlert && overdueCount > 0 && (
            <div className="flex items-center gap-2.5 bg-red-50 border border-red-200/60 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 mb-4">
              <AlertCircle size={14} className="flex-shrink-0 text-red-500" />
              <span className="text-[12px] sm:text-[13px] text-red-600 font-medium flex-1">
                {overdueCount} task{overdueCount !== 1 ? "s are" : " is"} overdue.
              </span>
              <button onClick={() => setOverdueAlert(false)} className="text-red-400 hover:text-red-600"><X size={14} /></button>
            </div>
          )}

          {loading && <div className="flex items-center justify-center py-12"><Loader size={32} className="animate-spin text-[#009DB9]" /></div>}
          {error && !loading && <div className="p-4 mb-4 text-sm text-red-600 rounded-lg bg-red-50">{error}</div>}

          {/* ── BOARD VIEW ── */}
          {!loading && !error && view === "board" && (
            <div className="flex gap-4 pb-4 overflow-x-auto">
              {BOARD_COLS.map((col) => {
                const colTasks = getTasksByStatus(col.id);
                return (
                  <div key={col.id} className="flex-shrink-0 w-[280px] sm:w-[320px] xl:w-[360px] flex flex-col bg-white rounded-2xl border border-neutral-100 overflow-hidden"
                    style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
                    <div className="flex items-center gap-2 px-3 py-3 border-b border-neutral-100 sm:px-4">
                      <span className="flex-shrink-0 w-2 h-2 rounded-full" style={{ background: col.color }} />
                      <span className="text-[13px] font-bold text-neutral-800 flex-1">{col.label}</span>
                      <span className="text-[11px] text-neutral-400">{colTasks.length}</span>
                      <button onClick={() => setShowAdd(true)} className="text-neutral-400 hover:text-neutral-600"><PlusSquare size={14} /></button>
                    </div>
                    <div className="flex-1 p-3 max-h-[calc(100vh-280px)] overflow-y-auto">
                      {colTasks.map((task) => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          contactInfo={contacts.find(c => c.id === task.contactId)}
                          assignedToName={getUserName(task.assignedTo, users, currentUser?.id)}
                          currentUserRole={currentUser?.role}
                          onMoveClick={(t) => setMoveModal({ open: true, task: t })}
                          onEditClick={(t) => { setSelectedTask(t); setShowEdit(true); }}
                          onDeleteClick={(t) => handleDeleteTask(t.id)}
                        />
                      ))}
                      <button onClick={() => setShowAdd(true)}
                        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-medium text-neutral-400 border border-dashed border-neutral-200 hover:bg-neutral-50 hover:text-neutral-600 hover:border-neutral-300 transition-all">
                        <Plus size={12} strokeWidth={2} /> Add task
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── LIST VIEW ── */}
          {!loading && !error && view === "list" && (
            <div className="flex-1 overflow-x-auto bg-white border rounded-2xl border-neutral-100" style={{ boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>
              <div className="min-w-[900px]">
                <div className="grid items-center gap-3 px-4 sm:px-5 py-2.5 border-b border-neutral-100 bg-neutral-50/80"
                  style={{ gridTemplateColumns: getListGridColumns() }}>
                  <input type="checkbox" className="w-3.5 h-3.5 rounded accent-[#009DB9]" />
                  {getListHeaders().map((h, i) => (
                    <div key={i} className="text-[10px] sm:text-[11px] font-semibold text-neutral-400 uppercase tracking-wider">{h}</div>
                  ))}
                </div>

                {sortedFilteredTasks.length === 0 ? (
                  <div className="py-12 text-center text-[13px] text-neutral-400">No tasks found.</div>
                ) : (
                  sortedFilteredTasks.map((task) => {
                    const isOverdue  = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== "COMPLETED";
                    const isCompleted = task.status === "COMPLETED";
                    const contactInfo = contacts.find(c => c.id === task.contactId);
                    const priority    = PRIORITY_CHIP[task.priority] || PRIORITY_CHIP.MEDIUM;
                    const assignedToName = getUserName(task.assignedTo, users, currentUser?.id);

                    return (
                      <div key={task.id}
                        className="grid items-center gap-3 px-4 py-3 transition-colors border-b sm:px-5 border-neutral-100 hover:bg-sky-50/30"
                        style={{ gridTemplateColumns: getListGridColumns(), opacity: isCompleted ? 0.55 : 1 }}>

                        {/* Checkbox */}
                        <input type="checkbox" checked={isCompleted}
                          onChange={() => handleStatusChange(task.id, isCompleted ? "TODO" : "COMPLETED")}
                          className="w-3.5 h-3.5 rounded accent-[#009DB9] cursor-pointer" />

                        {/* Task title + description */}
                        <div>
                          <div className={`text-[12px] sm:text-[13px] font-medium ${isCompleted ? "line-through text-neutral-400" : "text-neutral-900"}`}>
                            {task.title}
                          </div>
                          {task.description && (
                            <div className="text-[10px] sm:text-[11px] text-neutral-400 mt-0.5 line-clamp-1">{task.description}</div>
                          )}
                        </div>

                        {/* Assigned To - Only show for non-SALES_REP */}
                        {!isSalesRep && (
                          <div className="flex items-center gap-1.5">
                            {assignedToName ? (
                              <>
                                <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[8px] font-bold flex-shrink-0"
                                  style={{ background: AVATAR_COLORS[Math.abs((task.assignedTo || "").length) % AVATAR_COLORS.length] }}>
                                  {assignedToName.substring(0, 2).toUpperCase()}
                                </div>
                                <span className="text-[11px] text-neutral-700 truncate max-w-[90px]">{assignedToName}</span>
                              </>
                            ) : (
                              <span className="text-[11px] text-neutral-400 italic">Unassigned</span>
                            )}
                          </div>
                        )}

                        {/* Contact */}
                        <div className="flex items-center gap-2">
                          {contactInfo ? (
                            <>
                              <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0"
                                style={{ background: AVATAR_COLORS[Math.abs(task.id?.length || 0) % AVATAR_COLORS.length] }}>
                                {contactInfo.company?.substring(0, 2).toUpperCase() || "NA"}
                              </div>
                              <div>
                                <div className="text-[11px] font-medium text-neutral-700 truncate max-w-[100px]">{contactInfo.company}</div>
                                <div className="text-[9px] text-neutral-400 truncate max-w-[100px]">
                                  {contactInfo.fullName || `${contactInfo.firstName} ${contactInfo.lastName}`}
                                </div>
                              </div>
                            </>
                          ) : (
                            <span className="text-[11px] text-neutral-400 italic">No contact</span>
                          )}
                        </div>

                        {/* Due Date */}
                        <div className={`text-[11px] sm:text-[12px] font-medium flex items-center gap-1 ${isOverdue ? "text-red-500" : "text-neutral-500"}`}>
                          {isOverdue && <AlertCircle size={11} strokeWidth={2} />}
                          {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : "—"}
                        </div>

                        {/* Status */}
                        <select value={task.status} onChange={(e) => handleStatusChange(task.id, e.target.value)}
                          className="h-7 border border-neutral-200 rounded-lg px-2 text-[11px] text-neutral-700 outline-none focus:border-[#009DB9] bg-white cursor-pointer">
                          <option value="TODO">To-do</option>
                          <option value="IN_PROGRESS">In Progress</option>
                          <option value="COMPLETED">Done</option>
                        </select>

                        {/* Priority */}
                        <div>
                          <span className="text-[10px] font-semibold rounded-full px-2 py-0.5" style={{ background: priority.bg, color: priority.color }}>
                            {priority.label}
                          </span>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1">
                          <button onClick={() => { setSelectedTask(task); setShowEdit(true); }} className="p-1 rounded hover:bg-neutral-100" title="Edit">
                            <Edit size={14} className="text-neutral-500" />
                          </button>
                          <button onClick={() => handleDeleteTask(task.id)} className="p-1 rounded hover:bg-red-50" title="Delete">
                            <Trash2 size={14} className="text-red-400" />
                          </button>
                          <button onClick={() => setMoveModal({ open: true, task })} className="p-1 rounded hover:bg-neutral-100" title="Move">
                            <MoreHorizontal size={14} className="text-neutral-400" />
                          </button>
                          <button onClick={() => handleViewTasksByDeal(task.dealId)} className="p-1 rounded hover:bg-neutral-100" title="View Deal Tasks">
                            <ArrowRight size={14} className="text-neutral-400" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}

                <div className="flex flex-col gap-2 px-4 py-3 border-t sm:flex-row sm:items-center sm:justify-between sm:px-5 border-neutral-100">
                  <span className="text-[11px] text-neutral-400">
                    {sortedFilteredTasks.length} tasks · {sortedFilteredTasks.filter(t => t.status !== "COMPLETED").length} open · {sortedFilteredTasks.filter(t => t.status === "COMPLETED").length} done
                  </span>
                  <div className="text-[11px] text-neutral-400">
                    {priorityFilter !== "all" && `Filter: ${priorityFilter} · `}
                    Sort: {SORT_OPTIONS.find(o => o.id === sortBy)?.label} ({sortOrder === "asc" ? "High to Low" : "Low to High"})
                    {!isAdminRole && " · Showing your tasks only"}
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      <AddTaskModal open={showAdd} onClose={() => setShowAdd(false)} onAdd={handleAddTask}
        contacts={contacts} deals={deals} users={users} currentUser={currentUser} />
      <EditTaskModal open={showEdit} onClose={() => { setShowEdit(false); setSelectedTask(null); }}
        task={selectedTask} onUpdate={handleUpdateTask} contacts={contacts} deals={deals} users={users} currentUser={currentUser} />
      <MoveTaskModal open={moveModal.open} task={moveModal.task}
        onClose={() => setMoveModal({ open: false, task: null })}
        onMove={(s) => handleMoveTask(moveModal.task?.id, s)} />
    </div>
  );
}